import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { cx } from '@emotion/css';
import { Autocomplete, TextField } from '@procurenetworks/procure-component-library';
import { CircularProgress } from '@mui/material'; // Added CircularProgress import from @mui/material
import FormLabel from 'app/components/Form/FormLabel';
import {
  AssetItemSchema,
  InventoryItemSchema,
  ItemFilters,
  ItemTypeEnum,
  SortOrderEnum,
  MediaSchema, // Ensure MediaSchema is available if not implicitly via other schemas
} from 'app/types/schema';
import Box from 'app/ui-components/Box';
import { useClient } from 'urql'; // For manual queries

import FormError from '../../../../components/Form/FormError';
// Import the new hook and necessary types
import { useOptimizedItemSearch, TransformResponseResult } from '../../../../utils/itemSearchCache'; 
import { getSupportedImageAttachments } from '../../../../utils/attachments';
import { SupportedOrderItemTypes } from '../../../orders/views/OrderRequest/components/OrderItemRequest/type';
// Import the query document and its types
import {
  useOrderItemInStockSelectQuery,
  OrderItemInStockSelectDocument, // GraphQL DocumentNode
  OrderItemInStockSelectQuery, // TypeScript type for the query result
  OrderItemInStockSelectQueryVariables, // TypeScript type for the query variables
} from '../../graphql/query/generated/orderItemInStockSelect';

type Option =
  | Pick<InventoryItemSchema, 'id' | 'sku' | 'title' | 'attachments'>
  | Pick<AssetItemSchema, 'id' | 'sku' | 'title' | 'attachments'>;

interface SelectProps {
  className?: string;
  error?: FieldError | undefined;
  label?: string;
  isRequired?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  value: string; // ID of the selected item
  onBlur?: () => void;
  onFocus?: () => void;
  onChange: (values: string, option?: Option) => void;
  placeholder?: string;
  filters?: ItemFilters;
  renderInput?: (params: any) => React.ReactElement;
  tenantIds?: string[];
  partnerTenantId?: string;
  assetItemsOnly?: boolean;
  pickableThroughOrderRequest?: boolean;
  name?: string;
}

const getOptionLabel = (option: Option) => `${option.title} (${option.sku})`;
const getOptionValue = (option: Option) => option.id;

export const OrderFormItemInStock = forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
  const {
    className,
    label,
    value,
    isRequired,
    disabled,
    onBlur,
    onChange,
    error,
    placeholder = '',
    autoFocus,
    filters,
    onFocus,
    renderInput,
    tenantIds,
    partnerTenantId,
    pickableThroughOrderRequest,
    assetItemsOnly = false,
    name,
  } = props;

  const client = useClient();
  const [localInputValue, setLocalInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<Option | null>(null);

  const omitTypes = useMemo(
    () => (assetItemsOnly ? [ItemTypeEnum.Inventory, ItemTypeEnum.InventoryKit] : []),
    [assetItemsOnly]
  );

  const searchFilters = useMemo(() => ({
    types: SupportedOrderItemTypes.filter((item) => !omitTypes.includes(item)),
    ...filters,
    pickableThroughOrderRequest,
    tenantIds: tenantIds || undefined,
    partnerTenantId,
  }), [omitTypes, filters, pickableThroughOrderRequest, tenantIds, partnerTenantId]);

  // --- Logic for fetching selected item's details (if value is provided) ---
  const [{ fetching: selectedItemFetching, data: selectedData }] = useOrderItemInStockSelectQuery({
    variables: {
      filters: {
        itemIds: value ? [value] : [],
        pickableThroughOrderRequest,
        tenantIds: tenantIds || undefined,
        partnerTenantId,
      },
      limit: 1, // Only need one item
    },
    pause: !value || Boolean(value && selectedValue && selectedValue.id === value),
  });

  useEffect(() => {
    if (value && selectedData?.items.edges?.[0]?.node) {
      const selectedNode = selectedData.items.edges[0].node as Option;
      setSelectedValue(selectedNode);
      // setLocalInputValue(getOptionLabel(selectedNode)); // Optionally set input value
    } else if (!value) {
      setSelectedValue(null);
      // setLocalInputValue(''); // Clear input if value is cleared externally
    }
  }, [value, selectedData, setSelectedValue]);
  
  useEffect(() => {
    if (value === '') {
      setSelectedValue(null);
      // setLocalInputValue(''); // Also clear input if value becomes empty string
    }
  }, [value]);


  // --- Setup for useOptimizedItemSearch ---
  const transformResponse = useCallback(
    (
      response: OrderItemInStockSelectQuery,
      itemsPerPageFromHook: number
    ): TransformResponseResult<Option> => {
      const items = response.items?.edges?.map(({ node }) => {
        // Ensure attachments is always an array, even if null/undefined from GQL
        return {
          ...node,
          attachments: ('attachments' in node && node.attachments) ? node.attachments as MediaSchema[] : [],
        } as Option;
      }) || [];
      // Use pageInfo.hasNextPage if available, otherwise fallback to old logic
      const hasNextPage = response.items?.pageInfo?.hasNextPage ?? (items.length === itemsPerPageFromHook);
      return {
        items,
        hasNextPage,
      };
    },
    []
  );

  const getVariables = useCallback(
    (
      currentSearchTerm: string,
      currentFilters: Record<string, any>, // These are the searchFilters from the component combined with hook's internal filters
      page: number, // 1-indexed page number from useOptimizedItemSearch
      limit: number // itemsPerPage from useOptimizedItemSearch options
    ): OrderItemInStockSelectQueryVariables => {
      return {
        filters: {
          search: currentSearchTerm || undefined, // Use undefined for empty search term if API prefers that
          ...currentFilters,
        },
        sorts: [
          { sortField: 'sku', sortOrder: SortOrderEnum.Asc },
          { sortField: 'title', sortOrder: SortOrderEnum.Asc },
        ],
        limit: limit,
        page: page, // Pass the 1-indexed page number to the query
      };
    },
    []
  );

  const executeQueryWrapper = useCallback(
    async (variables: OrderItemInStockSelectQueryVariables) => {
      const result = await client
        .query<OrderItemInStockSelectQuery, OrderItemInStockSelectQueryVariables>(
          OrderItemInStockSelectDocument,
          variables
        )
        .toPromise();
      if (result.error) throw result.error;
      if (!result.data) throw new Error('No data returned from query');
      return result.data;
    },
    [client]
  );

  const { displayedItems, isLoadingInitial, isLoadingMore, hasNextPage, loadMore, error: searchHookError, setSearchTerm } = 
    useOptimizedItemSearch<Option, OrderItemInStockSelectQueryVariables, OrderItemInStockSelectQuery>(
      '', // initialSearchTerm passed as the first argument
      searchFilters, // initialFilters passed as the second argument
      { // options object passed as the third argument
        allowEmptySearch: true,
        itemsPerPage: 20,
        executeQuery: executeQueryWrapper,
        transformResponse,
        getVariables,
        debounceDelay: 300,
      }
    );

  useEffect(() => {
    // Initialize search term if there's an initial value for the Autocomplete
    if (value && selectedValue) {
      const initialLabel = getOptionLabel(selectedValue);
      setLocalInputValue(initialLabel);
      setSearchTerm(initialLabel); // Set initial search term for the hook if needed
    } else if (!value) {
        setLocalInputValue('');
        // setSearchTerm(''); // Let user input drive search term from empty state
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedValue]); // Removed setSearchTerm from deps to avoid loops on init


  // Effect to handle external value changes or initial value loading
  useEffect(() => {
    if (value && selectedData?.items.edges?.[0]?.node) {
      const selectedNode = selectedData.items.edges[0].node as Option;
      setSelectedValue(selectedNode);
      // setLocalInputValue(getOptionLabel(selectedNode)); // Moved to separate effect to avoid issues with setSearchTerm
    } else if (!value) {
      setSelectedValue(null);
      // setLocalInputValue('');
    }
  }, [value, selectedData]);

  useEffect(() => {
    if (value === '') {
      setSelectedValue(null);
      setLocalInputValue('');
      setSearchTerm(''); // Clear search term in hook when value is externally cleared
    }
  }, [value, setSearchTerm]);


  if (searchHookError) {
    // Handle or log searchHookError, e.g., show a message to the user
    console.error("Search query error from hook:", searchHookError);
  }

  const onValueChangeCallback = useCallback(
    (event: React.SyntheticEvent, option: Option | null) => {
      if (option) {
        setSelectedValue(option);
        onChange(option.id, option);
        const label = getOptionLabel(option);
        setLocalInputValue(label); 
        // setSearchTerm(label); // Setting search term here might trigger an immediate re-fetch based on selection. 
                               // This might be desired or might be redundant if the list already contains the item.
                               // For now, let input changes drive the search term primarily.
      } else {
        setSelectedValue(null);
        onChange('', undefined);
        setLocalInputValue(''); 
        setSearchTerm('');    // Reset search term on clear
      }
    },
    [onChange, setSearchTerm]
  );

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    // Check if the user has scrolled to the bottom of the listbox
    if (listboxNode.scrollTop + listboxNode.clientHeight + 1 >= listboxNode.scrollHeight) {
      if (hasNextPage && !isLoadingMore) {
        loadMore();
      }
    }
  };

  // renderInput prop is passed through, or a default TextField is used.
  // autoFocus is handled by the TextField inside renderInput or the default one.

  return (
    <Box className={cx('flex flex-col', className)} ref={ref}>
      {label && <FormLabel isRequired={isRequired}>{label}</FormLabel>}
      <Autocomplete
        // autoFocus={autoFocus} // Removed: autoFocus should be on the TextField
        disabled={disabled || selectedItemFetching}
        value={selectedValue}
        options={displayedItems}
        loading={isLoadingInitial || isLoadingMore || selectedItemFetching}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onValueChangeCallback} // Use the renamed callback
        inputValue={localInputValue}
        onInputChange={(event, newInputValue, reason) => {
          setLocalInputValue(newInputValue); // Always update local input value
          if (reason === 'input') {
            setSearchTerm(newInputValue); // Update search term for the hook
          } else if (reason === 'clear') {
            setSearchTerm('');
            // onChange('', undefined); // This is handled by onValueChangeCallback when newInputValue is null
          }
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, val) => option.id === val?.id}
        placeholder={placeholder} // placeholder is passed to TextField in renderInput
        label={label || ' '} // Added label prop, using component's label or a space to satisfy type
        ListboxProps={{
          onScroll: handleScroll,
        }}
        renderOption={(optionProps, option: Option) => {
          const attachments = (option.attachments || []) as MediaSchema[];
          const imageAttachments = getSupportedImageAttachments(attachments);
          const imageUrl = imageAttachments.length ? imageAttachments[0].url : undefined;
          // const showImageBox = selectedValue?.id !== option?.id; // Logic from user's old snippet

          return (
            <li {...optionProps} key={option.id} style={{ display: 'flex', alignItems: 'center' }}>
              {imageUrl ? (
                <img
                  alt={option.title}
                  className="mw-[50px] mh-[50px] mr-[10px] h-[50px] w-[50px] object-contain"
                  src={imageUrl}
                  loading="lazy"
                />
              ) : (
                <div className="mw-[50px] mh-[50px] mr-[10px] h-[50px] w-[50px] flex items-center justify-center bg-gray-100 text-gray-400">
                  {/* Consider adding a placeholder icon here if available */}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </div>
              )}
              <span style={{ marginLeft: '10px' }}>
                {option.title} ({option.sku})
              </span>
            </li>
          );
        }}
        renderInput={(
          params: any // params from Autocomplete
        ) => {
          if (renderInput) {
            return renderInput(params); // Custom renderInput provided
          }

          // Default TextField rendering
          return (
            <TextField
              {...params} // Includes ref, inputProps from Autocomplete
              name={name}
              autoFocus={autoFocus} // Apply autoFocus here
              placeholder={placeholder}
              error={!!error || !!searchHookError} // Combine field error and hook error
              helperText={error?.message || searchHookError?.message}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(isLoadingInitial || isLoadingMore || selectedItemFetching) ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
        filterOptions={(x) => x} // Disable client-side filtering
      />
      {error && <FormError error={error} />}
      {searchHookError && !error && <FormError error={{ message: searchHookError.message }} />}
    </Box>
  );
});

OrderFormItemInStock.displayName = 'OrderFormItemInStock';
export default OrderFormItemInStock;
